import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ResizeObserver | null} */
  #summaryResizeObserver = null;

  /** @type {number | null} */
  #updateStickyStateTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#ensureSectionWrapper();
    this.#observeSummaryResize();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#scheduleUpdateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#summaryResizeObserver?.disconnect();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#scheduleUpdateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #observeSummaryResize() {
    this.#summaryResizeObserver = new ResizeObserver(() => {
      this.#scheduleUpdateStickyState();
    });

    const tryObserve = () => {
      const summary = this.querySelector('.cart-drawer__summary');
      if (summary) {
        this.#summaryResizeObserver.observe(summary);

        const childObserver = new MutationObserver(() => {
          summary.querySelectorAll('*').forEach((el) => {
            this.#summaryResizeObserver.observe(el);
          });
          this.#scheduleUpdateStickyState();
        });
        childObserver.observe(summary, { childList: true, subtree: true });
      } else {
        requestAnimationFrame(tryObserve);
      }
    };

    tryObserve();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.#ensureSectionWrapper();
      this.showDialog();
    }
  };

  #ensureSectionWrapper() {
    const cartItems = this.querySelector('cart-items-component[data-drawer]');
    if (!cartItems) return;

    const sectionId = cartItems.dataset.sectionId;
    if (!sectionId) return;

    const wrapperId = `shopify-section-${sectionId}`;
    if (document.getElementById(wrapperId)) return;

    const drawerInner = this.querySelector('[data-hydration-key="cart-drawer-inner"]');
    if (!drawerInner) return;

    const wrapper = document.createElement('div');
    wrapper.id = wrapperId;
    drawerInner.parentNode.insertBefore(wrapper, drawerInner);
    wrapper.appendChild(drawerInner);
  }

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #scheduleUpdateStickyState = () => {
    if (this.#updateStickyStateTimer) cancelAnimationFrame(this.#updateStickyStateTimer);
    this.#updateStickyStateTimer = requestAnimationFrame(() => {
      this.#updateStickyStateTimer = requestAnimationFrame(() => {
        this.#updateStickyState();
        // Re-check after payment buttons have had time to render
        setTimeout(() => this.#updateStickyState(), 500);
        setTimeout(() => this.#updateStickyState(), 1500);
      });
    });
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}